import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import { addTraveller, deleteTraveller, editTraveller, getTravellers } from '../../../api/endpoints/traveller';
import type { Traveller } from '../../../api/types/traveller';
import { ApiErrorAlert } from '../../../components/ApiErrorAlert';
import { ListSearchField } from '../../../components/ListSearchField';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { PhoneField } from '../../../components/PhoneField';

const FIELDS: { name: keyof Traveller; label: string }[] = [
  { name: 'firstName', label: 'Ad' },
  { name: 'lastName', label: 'Soyad' },
  { name: 'gender', label: 'Cinsiyet (Male / Female)' },
  { name: 'birthDate', label: 'Doğum Tarihi (GG.AA.YYYY)' },
  { name: 'nationality', label: 'Uyruk (TR)' },
  { name: 'identity', label: 'T.C. Kimlik No' },
  { name: 'passport', label: 'Pasaport No' },
  { name: 'passportExpireDate', label: 'Pasaport Geçerlilik (GG.AA.YYYY)' },
  { name: 'email', label: 'E-posta' },
  { name: 'phone', label: 'Telefon' },
  { name: 'frequentFlyerNumber', label: 'Mil Kart No' },
  { name: 'additionalInformation', label: 'HES Kodu / Not' },
];

/**
 * "Kayıtlı Yolcu Listesi" — `Traveller/{GetTravellers,AddTraveller,EditTraveller,
 * DeleteTraveller}` (+ optional name / T.C. filter). Sidebar-gated to master GSA (see
 * ../index.tsx). Shares the `Traveller` shape with the Air passenger step's
 * `TravellerAutocomplete`. The edit dialog doubles as the create form — `editing.id`
 * decides Add vs Edit.
 */
export default function TravellerListPage() {
  const qc = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [search, setSearch] = useState('');
  const query = useQuery<Traveller[]>({
    queryKey: ['travellers', 'list'],
    queryFn: () => getTravellers().then((r) => r.travellers ?? []),
  });
  const [editing, setEditing] = useState<Traveller | null>(null);
  const [form, setForm] = useState<Traveller>({});
  const [deleteTarget, setDeleteTarget] = useState<Traveller | null>(null);

  const term = search.trim().toLowerCase();
  const all: Traveller[] = query.data ?? [];
  const rows = all.filter((t) => {
    if (!term) return true;
    return [t.firstName, t.lastName, t.fullName, t.identity, t.passport, t.email, t.phone]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(term));
  });

  const isCreate = !editing?.id;
  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = { ...editing, ...form };
      return isCreate ? addTraveller(payload) : editTraveller(payload);
    },
    onSuccess: (res) => {
      enqueueSnackbar(res.successMessage ?? (isCreate ? 'Yolcu eklendi.' : 'Yolcu kaydedildi.'), {
        variant: 'success',
      });
      setEditing(null);
      qc.invalidateQueries({ queryKey: ['travellers'] });
    },
    onError: (err) => enqueueSnackbar(err instanceof Error ? err.message : 'Kayıt başarısız.', { variant: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteTraveller(deleteTarget?.id ?? ''),
    onSuccess: (res) => {
      enqueueSnackbar(res.successMessage ?? 'Yolcu silindi.', { variant: 'success' });
      setDeleteTarget(null);
      qc.invalidateQueries({ queryKey: ['travellers'] });
    },
    onError: (err) => enqueueSnackbar(err instanceof Error ? err.message : 'Silme başarısız.', { variant: 'error' }),
  });

  const openForm = (row: Traveller) => {
    setEditing(row);
    setForm(row);
  };

  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Kayıtlı Yolcu Listesi</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setForm({ nationality: 'TR' });
            setEditing({});
          }}
        >
          Yeni Yolcu
        </Button>
      </Stack>

      <Box sx={{ mb: 2 }}>
        <ListSearchField value={search} onChange={setSearch} placeholder="Ad, soyad veya T.C. kimlik ile ara" />
      </Box>

      <ApiErrorAlert error={query.error} sx={{ mb: 2 }} />
      {query.isPending && <CircularProgress size={24} />}

      {query.isSuccess && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Ad Soyad</TableCell>
                <TableCell>Kimlik / Pasaport</TableCell>
                <TableCell>İletişim</TableCell>
                <TableCell align="right">İşlem</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                      {search ? 'Eşleşen kayıtlı yolcu yok.' : 'Kayıtlı yolcu yok.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {rows.map((row, index) => (
                <TableRow key={row.id ?? index} hover>
                  <TableCell>
                    {row.fullName || [row.firstName, row.lastName].filter(Boolean).join(' ') || '—'}
                  </TableCell>
                  <TableCell>{row.identity || row.passport || '—'}</TableCell>
                  <TableCell>{[row.email, row.phone].filter(Boolean).join(' · ') || '—'}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openForm(row)} aria-label="Düzenle">
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setDeleteTarget(row)}
                      disabled={!row.id}
                      aria-label="Sil"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{isCreate ? 'Yeni Yolcu' : 'Yolcu Düzenle'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {FIELDS.map((field) =>
              field.name === 'phone' ? (
                <PhoneField
                  key={field.name}
                  label={field.label}
                  value={(form[field.name] as string | undefined) ?? ''}
                  onChange={(v) => setForm((p) => ({ ...p, [field.name]: v }))}
                />
              ) : (
                <TextField
                  key={field.name}
                  size="small"
                  label={field.label}
                  value={(form[field.name] as string | undefined) ?? ''}
                  onChange={(e) => setForm((p) => ({ ...p, [field.name]: e.target.value }))}
                />
              )
            )}
            <ApiErrorAlert error={saveMutation.error} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)} disabled={saveMutation.isPending}>
            Vazgeç
          </Button>
          <Button variant="contained" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
            {saveMutation.isPending ? 'Kaydediliyor…' : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Yolcuyu sil"
        description={`${
          deleteTarget?.fullName || [deleteTarget?.firstName, deleteTarget?.lastName].filter(Boolean).join(' ')
        } kaydını silmek istediğinize emin misiniz?`}
        confirmLabel="Sil"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate()}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
}
